import React, { useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Toolbar,
  Avatar,
  IconButton,
  Stack,
  Divider,
  Container,
  useTheme,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteForever from "@mui/icons-material/DeleteForever";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { createUser } from "../../services/api"; // ensure this exists and accepts multipart/form-data

export default function AddUser() {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [errors, setErrors] = useState({});

  // validation
  // validation
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!/^[0-9+()\s-]{6,20}$/.test(form.phone)) e.phone = "Invalid phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
    setErrors((s) => ({ ...s, [e.target.name]: undefined }));
  };

  const handleAvatarChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Max avatar size 6MB",
      });
      return;
    }
    if (!/image\/(png|jpeg|jpg|webp)/.test(f.type)) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Only JPG / PNG / WEBP allowed",
      });
      return;
    }
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", phone: "" });
    removeAvatar();
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fix validation errors",
      });
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim());
      if (form.password) fd.append("password", form.password); // only if entered
      if (avatarFile) fd.append("avatar", avatarFile);

      // createUser should POST to your backend and accept multipart/form-data
      const res = await createUser(fd);

      setSnackbar({
        open: true,
        severity: "success",
        message: res?.data?.message || "User created successfully",
      });

      // optional: short delay then redirect to users list
      setTimeout(() => navigate("/users"), 900);
    } catch (err) {
      console.error("Add user error", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to create user";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <Box>
      {/* spacer for fixed header */}
      <Toolbar />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Add New User
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            background:
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fff",
            boxShadow: "0 6px 30px rgba(2,6,23,0.4)",
            display: "flex",
            overflow: "hidden",
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            {/* left avatar area */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{
                    width: 140,
                    height: 140,
                    bgcolor: avatarPreview ? "transparent" : "primary.main",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    fontSize: 54,
                  }}
                >
                  {!avatarPreview && (form.name?.[0]?.toUpperCase() || "U")}
                </Avatar>

                <Typography variant="subtitle1" fontWeight={600}>
                  Profile Photo
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ px: 1 }}
                >
                  Optional — JPG / PNG / WEBP. Max 6MB. Use a clear headshot for
                  identification.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<PhotoCamera />}
                    sx={{ textTransform: "none" }}
                    size="small"
                  >
                    Upload
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleAvatarChange}
                      ref={fileRef}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForever />}
                    onClick={removeAvatar}
                    sx={{ textTransform: "none" }}
                    size="small"
                    disabled={!avatarPreview}
                  >
                    Remove
                  </Button>
                </Stack>
              </Box>
            </Grid>

            {/* right form area */}
            <Grid item xs={12} md={8}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ height: "100%" }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Full Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="filled"
                      InputProps={{ disableUnderline: true }}
                      error={Boolean(errors.name)}
                      helperText={errors.name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="filled"
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                      InputProps={{ disableUnderline: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      fullWidth
                      required
                      variant="filled"
                      error={Boolean(errors.phone)}
                      helperText={errors.phone}
                      InputProps={{ disableUnderline: true }}
                      placeholder="+1 555 012 3456"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password (optional)"
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={handleChange}
                      fullWidth
                      variant="filled"
                      error={Boolean(errors.password)}
                      helperText={
                        errors.password ||
                        "Leave blank to auto-generate or skip"
                      }
                      InputProps={{ disableUnderline: true }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PersonAddIcon />
                        )
                      }
                      disabled={loading}
                      sx={{
                        textTransform: "none",
                        px: 3,
                        bgcolor: "#F6C84C",
                        color: theme.palette.getContrastText("#F6C84C"),
                        "&:hover": { bgcolor: "#e0b645" },
                      }}
                    >
                      {loading ? "Creating..." : "Create User"}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<CheckIcon />}
                      onClick={resetForm}
                      disabled={loading}
                      sx={{ textTransform: "none" }}
                    >
                      Reset
                    </Button>

                    <Box sx={{ flex: 1 }} />

                    <Button
                      variant="text"
                      startIcon={<CloseIcon />}
                      onClick={() => navigate("/users")}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
